import React, { ReactElement } from 'react';

import { AnimatePresence } from 'framer-motion';
import styled from 'styled-components';

import { Button, UnderlinedGhostButton } from 'chime-ui/components/Button';
import { iconsPath } from 'chime-ui/components/Icons';
import { useBottomSheet } from 'chime-ui/components/Modal';
import { Text, Title } from 'chime-ui/components/Typography';
import { colors } from 'chime-ui/system';
import media from 'chime-ui/system/media';

import Layout, { ContentWrapper } from 'components/Layout';
import Header from 'components/Layout/Header';
import usePayAnyoneConfirmationPage from 'components/SubPages/Confirmation/Pay/usePayAnyoneConfirmationPage';

import useP2PEnrollmentFlow from 'hooks/useP2PEnrollmentFlow';
import { useUserContext } from 'hooks/useUserContext';

function ConfirmationWithoutBonus(): ReactElement {
  const { trackCtaButtonClick } = usePayAnyoneConfirmationPage();
  const [userData] = useUserContext();
  const { amount } = userData;
  const { activateP2pEnrollmentAndNavigate } = useP2PEnrollmentFlow(false);

  const { Modal } = useBottomSheet();
  const {
    isShowing: isDirectDepositModalShowing,
    handleShow: showDirectDepositModal,
    handleDismiss: dismissDirectDepositModal,
  } = useBottomSheet();

  return (
    <Layout header={<Header />}>
      <ContentWrapper>
        <ImageLayout>
          <img
            src={`${iconsPath}confirmationPageMessage.svg`}
            alt={`Money's on the way`}
          />
        </ImageLayout>
        <ContentLayout>
          <Title size={'medium'}>
            Cha-ching!
            <br />
            <Text color={colors.darkGreen}>${amount}</Text> is coming!
          </Title>
          <Text size={'standard'}>
            Get even more from Chime like getting paid up to 2 days early with
            direct deposit <br />
            <UnderlinedGhostButton onClick={showDirectDepositModal}>
              See details
            </UnderlinedGhostButton>
          </Text>
        </ContentLayout>
        <ButtonLayout>
          <Button
            onClick={() =>
              activateP2pEnrollmentAndNavigate(trackCtaButtonClick)
            }
          >
            Join Chime
          </Button>
        </ButtonLayout>
      </ContentWrapper>
      <AnimatePresence>
        {isDirectDepositModalShowing && (
          <Modal
            showX
            handleDismiss={dismissDirectDepositModal}
            dismissText={'Dismiss'}
          >
            <ModalContent>
              <ModalText>
                <Text size={'standard'}>
                  Early access to direct deposit funds depends on the timing of
                  the submission of the payment file from the payer. We
                  generally make these funds available on the day the payment
                  file is received, which may be up to 2 days earlier than the
                  scheduled payment date.
                </Text>
              </ModalText>
            </ModalContent>
          </Modal>
        )}
      </AnimatePresence>
    </Layout>
  );
}

const ModalContent = styled.div`
  padding-top: 2.5rem;
`;

const ButtonLayout = styled.div`
  ${media.mobileOnly`
    position: fixed;
    bottom: 0;
    right: 0;
    left: 0;
  `};

  background-color: ${colors.white};
  padding: 1.5rem 0;
  margin: 1rem 1.5rem 0 1.5rem;
  border-top: 1px solid ${colors.lightGrey};
`;

const ModalText = styled.div`
  margin-bottom: 2rem;
`;

const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: 1.5rem;
`;

const ImageLayout = styled.div`
  display: flex;
  justify-content: center;
`;

export default ConfirmationWithoutBonus;
